import { ref, computed } from 'vue';
import {
  fetchSubscriptionOverview,
  fetchWallet,
  fetchBillingOrders,
  fetchBillingCashPayments,
  createBillingOrder,
  createWalletTopup,
  createIdempotencyKey,
  normalizeOverview,
} from '../api';
import { mapApiError } from '../helpers/purchaseGuards';

export const useBillingOverview = accountIdRef => {
  const loading = ref(false);
  const error = ref('');
  const errorCode = ref('');
  const overview = ref(null);

  const ledgerLoading = ref(false);
  const ledgerError = ref('');
  const ledger = ref([]);

  const ordersLoading = ref(false);
  const ordersError = ref('');
  const orders = ref([]);

  const invoicesLoading = ref(false);
  const invoicesError = ref('');
  const invoices = ref([]);

  const submitting = ref(false);
  const topupSubmitting = ref(false);

  const subscription = computed(() => overview.value?.subscription || null);
  const seats = computed(() => overview.value?.seats || null);
  const wallet = computed(() => overview.value?.wallet || null);
  const credits = computed(() => overview.value?.credits || null);
  const catalog = computed(() => overview.value?.catalog || null);
  const isPro = computed(
    () => String(subscription.value?.plan_code || '').toLowerCase() === 'pro'
  );
  const balanceCents = computed(() => Number(wallet.value?.balance_cents) || 0);
  const spendFrozen = computed(
    () => wallet.value?.wallet_spend_frozen === true
  );

  const loadOverview = async () => {
    loading.value = true;
    error.value = '';
    errorCode.value = '';
    try {
      const data = await fetchSubscriptionOverview(accountIdRef.value);
      overview.value = normalizeOverview(data);
    } catch (err) {
      errorCode.value = mapApiError(err);
      error.value = errorCode.value;
      overview.value = null;
    } finally {
      loading.value = false;
    }
  };

  const loadLedger = async () => {
    ledgerLoading.value = true;
    ledgerError.value = '';
    try {
      const data = await fetchWallet(accountIdRef.value);
      ledger.value = data.ledger || [];
      if (overview.value) {
        overview.value = {
          ...overview.value,
          wallet: {
            ...(overview.value.wallet || {}),
            balance_cents: data.balance_cents,
            currency: data.currency || 'USD',
            pending_collection_cents: data.pending_collection_cents,
            wallet_spend_frozen: data.wallet_spend_frozen,
          },
        };
      }
    } catch (err) {
      ledgerError.value = mapApiError(err);
      ledger.value = [];
    } finally {
      ledgerLoading.value = false;
    }
  };

  const loadOrders = async () => {
    ordersLoading.value = true;
    ordersError.value = '';
    try {
      const data = await fetchBillingOrders(accountIdRef.value);
      orders.value = data.payload || [];
    } catch (err) {
      ordersError.value = mapApiError(err);
      orders.value = [];
    } finally {
      ordersLoading.value = false;
    }
  };

  const loadInvoices = async () => {
    invoicesLoading.value = true;
    invoicesError.value = '';
    try {
      invoices.value = await fetchBillingCashPayments(accountIdRef.value);
    } catch (err) {
      invoicesError.value = mapApiError(err);
      invoices.value = [];
    } finally {
      invoicesLoading.value = false;
    }
  };

  const refreshAll = async () => {
    await Promise.all([
      loadOverview(),
      loadLedger(),
      loadOrders(),
      loadInvoices(),
    ]);
  };

  const purchase = async payload => {
    if (submitting.value) {
      return { ok: false, reason: 'submitting' };
    }
    submitting.value = true;
    try {
      const body = {
        ...payload,
        idempotency_key: payload.idempotency_key || createIdempotencyKey(),
      };
      const result = await createBillingOrder(accountIdRef.value, body);
      await refreshAll();
      return { ok: true, result };
    } catch (err) {
      return { ok: false, reason: mapApiError(err), error: err };
    } finally {
      submitting.value = false;
    }
  };

  const startTopup = async amountCents => {
    if (topupSubmitting.value) {
      return { ok: false, reason: 'submitting' };
    }
    topupSubmitting.value = true;
    try {
      const result = await createWalletTopup(accountIdRef.value, amountCents);
      if (result?.checkout_url) {
        window.location.assign(result.checkout_url);
        return { ok: true, result, redirecting: true };
      }
      return { ok: false, reason: 'invalid_purchase' };
    } catch (err) {
      return { ok: false, reason: mapApiError(err), error: err };
    } finally {
      topupSubmitting.value = false;
    }
  };

  return {
    loading,
    error,
    errorCode,
    overview,
    subscription,
    seats,
    wallet,
    credits,
    catalog,
    isPro,
    balanceCents,
    spendFrozen,
    ledgerLoading,
    ledgerError,
    ledger,
    ordersLoading,
    ordersError,
    orders,
    invoicesLoading,
    invoicesError,
    invoices,
    submitting,
    topupSubmitting,
    loadOverview,
    loadLedger,
    loadOrders,
    loadInvoices,
    refreshAll,
    purchase,
    startTopup,
  };
};
